import { getDevtools } from '@/modules/devtools'

import { BatchQueue } from './batch-queue'
import {
    type PlaygamaRumEvent,
    type RawPlaygamaCTREvent,
    type RawPlaygamaEvent,
    type RawPlaygamaUIEvent,
    type RawRumMetricData,
    type RumMetricResult,
} from './types'

export type AnalyticsClient = {
    log: (event: RawPlaygamaEvent) => void
    logAndFlush: (event: RawPlaygamaEvent) => void
    logUiEvent: (event: RawPlaygamaUIEvent) => void
    logCTREvent: (event: RawPlaygamaCTREvent) => void
    startRumEvent: (metric: RawRumMetricData) => (status: RumMetricResult) => void
    registerPageHideFallbackEvent: (event: RawPlaygamaEvent) => () => void
}

const BATCH_CONFIG = {
    flushIntervalMs: 5000,
    maxBatchSizeBytes: 25000,
    maxBatchSizeCount: 20,
}

export class Analytics implements AnalyticsClient {
    private portalEventsQueue: BatchQueue<RawPlaygamaEvent>
    private rumEventsQueue: BatchQueue<PlaygamaRumEvent>
    private uiEventsQueue: BatchQueue<RawPlaygamaUIEvent>
    private ctrEventsQueue: BatchQueue<RawPlaygamaCTREvent>
    private pendingUnloadEvents = new Map<symbol, RawPlaygamaEvent>()

    constructor(baseEndpoint: string) {
        const batchConfig = {
            enabled: process.env.NODE_ENV === 'production',
            ...BATCH_CONFIG,
        }

        this.portalEventsQueue = new BatchQueue<RawPlaygamaEvent>(`${baseEndpoint}/portal/events`, batchConfig)
        this.rumEventsQueue = new BatchQueue<PlaygamaRumEvent>(`${baseEndpoint}/rum/events`, batchConfig)
        this.uiEventsQueue = new BatchQueue<RawPlaygamaUIEvent>(`${baseEndpoint}/portal/ui/events`, batchConfig)
        this.ctrEventsQueue = new BatchQueue<RawPlaygamaCTREvent>(`${baseEndpoint}/portal/ctr/events`, batchConfig)

        this.setupPageUnloadFlush()
    }

    log(event: RawPlaygamaEvent) {
        this.portalEventsQueue.add(event)
    }

    logAndFlush(event: RawPlaygamaEvent) {
        this.portalEventsQueue.add(event)
        this.portalEventsQueue.flush()
    }

    logUiEvent(event: RawPlaygamaUIEvent) {
        this.uiEventsQueue.add(event)
    }

    logCTREvent(event: RawPlaygamaCTREvent) {
        getDevtools()?.record('analytics', `ctr_${event.eventName}`, event)
        this.ctrEventsQueue.add(event)
    }

    private logRum(event: PlaygamaRumEvent) {
        getDevtools()?.record('analytics', `rum_${event.operationName}`, event)
        this.rumEventsQueue.add(event)
    }

    startRumEvent(metric: RawRumMetricData) {
        const start = Date.now()
        let isSent = false

        return (status: RumMetricResult) => {
            if (isSent) {
                console.trace('Sending metric after its already sent')
                return
            }
            const now = Date.now()
            this.logRum({
                ...metric,
                ...status,
                durationMs: now - start,
                finishTimestamp: now,
            })
            isSent = true
        }
    }

    registerPageHideFallbackEvent(event: RawPlaygamaEvent): () => void {
        const key = Symbol()
        this.pendingUnloadEvents.set(key, event)
        return () => this.pendingUnloadEvents.delete(key)
    }

    private setupPageUnloadFlush() {
        const flushAll = () => {
            for (const event of this.pendingUnloadEvents.values()) {
                this.portalEventsQueue.add(event)
            }
            this.pendingUnloadEvents.clear()

            this.portalEventsQueue.flush()
            this.rumEventsQueue.flush()
            this.uiEventsQueue.flush()
            this.ctrEventsQueue.flush()
        }

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                flushAll()
            }
        })

        window.addEventListener('pagehide', flushAll)
    }
}
